/**
 * Sentry Error Tracking Configuration
 */

import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error tracking and performance monitoring
 */
export const initSentry = () => {
  // Only initialize in production or staging
  if (process.env.NODE_ENV === 'development') {
    console.log('Sentry not initialized in development mode');
    return;
  }

  const sentryDSN = process.env.REACT_APP_SENTRY_DSN;
  
  if (!sentryDSN) {
    console.warn('Sentry DSN not configured. Error tracking disabled.');
    return;
  }

  Sentry.init({
    dsn: sentryDSN,
    
    // Environment
    environment: process.env.REACT_APP_ENV || process.env.NODE_ENV,
    
    // Release tracking
    release: process.env.REACT_APP_VERSION || '0.1.0',
    
    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    
    // Performance sampling
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
    
    // Filtering
    beforeSend(event, hint) {
      // Filter out sensitive data
      if (event.request?.cookies) {
        delete event.request.cookies;
      }
      
      // Filter out known non-errors
      const error = hint.originalException;
      if (error && typeof error === 'object' && 'message' in error) {
        const message = (error as Error).message;
        
        // Ignore network errors that are handled
        if (message.includes('NetworkError') || 
            message.includes('Failed to fetch')) {
          return null;
        }
        
        // Ignore cancelled requests
        if (message.includes('AbortError')) {
          return null;
        }
      }
      
      // Remove sensitive data from URLs
      if (event.request?.url) {
        event.request.url = sanitizeUrl(event.request.url);
      }
      
      // Add user context without sensitive data
      const codiceFiscale = localStorage.getItem('codiceFiscale');
      if (codiceFiscale) {
        event.user = {
          id: hashString(codiceFiscale), // Hash the ID for privacy
        };
      }
      
      return event;
    },
    
    // Breadcrumbs configuration
    beforeBreadcrumb(breadcrumb) {
      // Filter out sensitive breadcrumbs
      if (breadcrumb.category === 'console' && 
          breadcrumb.level === 'debug') {
        return null;
      }
      
      // Sanitize data in breadcrumbs
      if (breadcrumb.data) {
        breadcrumb.data = sanitizeObject(breadcrumb.data);
      }
      
      return breadcrumb;
    },
    
    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      
      // Network errors (handled by app)
      'NetworkError',
      'Failed to fetch',
      
      // Safari specific
      'Non-Error promise rejection',
    ],
    
    // Denied URLs (don't track errors from these)
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      
      // Other browsers
      /^moz-extension:\/\//i,
      /^ms-browser-extension:\/\//i,
    ],
  });
};

/**
 * Capture exception with additional context
 */
export const captureException = (
  error: Error | unknown, 
  context?: Record<string, any>
) => {
  if (process.env.NODE_ENV === 'development') {
    console.error('Sentry would capture:', error, context);
    return;
  }
  
  Sentry.captureException(error, {
    contexts: {
      custom: sanitizeObject(context || {})
    }
  });
};

/**
 * Capture message with level
 */
export const captureMessage = (
  message: string, 
  level: Sentry.SeverityLevel = 'info'
) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`Sentry would capture ${level}:`, message);
    return;
  }
  
  Sentry.captureMessage(message, level);
};

/**
 * Add breadcrumb for tracking user actions
 */
export const addBreadcrumb = (breadcrumb: {
  message: string;
  category?: string;
  level?: Sentry.SeverityLevel;
  data?: Record<string, any>;
}) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('Sentry breadcrumb:', breadcrumb);
    return;
  }
  
  Sentry.addBreadcrumb({
    ...breadcrumb,
    data: sanitizeObject(breadcrumb.data || {})
  });
};

/**
 * Set user context (sanitized)
 */
export const setUserContext = (user: {
  id?: string;
  email?: string;
  username?: string;
}) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('Sentry user context:', user);
    return;
  }
  
  // Hash sensitive data
  const sanitizedUser: any = {};
  
  if (user.id) {
    sanitizedUser.id = hashString(user.id);
  }
  
  if (user.email) {
    // Partially mask email
    const [local, domain] = user.email.split('@');
    sanitizedUser.email = `${local.substring(0, 2)}***@${domain}`;
  }
  
  if (user.username) {
    sanitizedUser.username = hashString(user.username);
  }
  
  Sentry.setUser(sanitizedUser);
};

/**
 * Clear user context on logout
 */
export const clearUserContext = () => {
  if (process.env.NODE_ENV === 'development') {
    console.log('Sentry user context cleared');
    return;
  }
  
  Sentry.setUser(null);
};

/**
 * Profiling transaction
 */
export const startTransaction = (
  name: string, 
  op: string = 'navigation'
): any => {
  if (process.env.NODE_ENV === 'development') {
    console.log('Sentry transaction started:', name);
    return null;
  }
  
  // In newer versions, use startSpan instead
  return Sentry.startSpan({ name, op }, () => {
    // Transaction logic here
  });
};

/**
 * Helper function to hash sensitive strings
 */
function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString(36);
}

/**
 * Helper function to sanitize URLs
 */
function sanitizeUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    
    // Remove sensitive query parameters
    const sensitiveParams = [
      'token', 'password', 'secret', 'key', 'api_key',
      'codiceFiscale', 'cf', 'username', 'email'
    ];
    
    sensitiveParams.forEach(param => {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, '[REDACTED]');
      }
    });
    
    return urlObj.toString();
  } catch {
    return url;
  }
}

/**
 * Helper function to sanitize objects
 */
function sanitizeObject(obj: Record<string, any>): Record<string, any> {
  const sensitiveKeys = [
    'password', 'token', 'secret', 'key', 'api_key',
    'codiceFiscale', 'cf', 'authorization', 'cookie'
  ];
  
  const sanitized: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(obj)) {
    const lowerKey = key.toLowerCase();
    
    if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeObject(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}


export default {
  initSentry,
  captureException,
  captureMessage,
  addBreadcrumb,
  setUserContext,
  clearUserContext,
  startTransaction
};